import { useEffect, useState } from "react"

import type { iconButtonVariantsType } from "@productlane/ui"
import type { Project } from "@productlane/zero"
import { USER_COLOR_PALETTE } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { IconButton } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { ColorPicker } from "@/components/color-picker"
import { RoleCheck } from "@/components/shared/role-check"
import { getProjectIcon } from "@/lib/projects"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"

interface Props {
	project: Project
	iconSize?: "sm" | "default" | "lg" | "xl"
	className?: string
	iconVariant?: iconButtonVariantsType
	withBackground?: boolean
	/** Tints the displayed icon with this color instead of the project's own color. The color picker still edits project.color. */
	overrideColor?: string
}

const iconSizeMap = {
	sm: "h-3 w-3 min-h-3 min-w-3",
	default: "h-4 w-4 min-h-4 min-w-4",
	lg: "h-5 w-5 min-h-5 min-w-5",
	xl: "h-6 w-6 min-h-6 min-w-6",
}

export const ProjectIconSelect = ({
	project,
	className,
	iconSize = "default",
	iconVariant = "ghost-brightness",
	withBackground = false,
	overrideColor,
}: Props) => {
	const [icon, setIcon] = useState(project.icon)
	const [color, setColor] = useState(
		project.color || USER_COLOR_PALETTE.Teal.hex,
	)
	const zero = useZero()
	const { role } = useRole()
	const [open, setOpen] = useState(false)

	const Icon = getProjectIcon(icon)

	useEffect(() => {
		setIcon(project.icon)
	}, [project.icon])

	useEffect(() => {
		setColor(project.color || USER_COLOR_PALETTE.Teal.hex)
	}, [project.color])

	const iconElement = (
		<Icon
			className={cn(
				iconSizeMap[iconSize],
				className,
				"overflow-visible transition-all",
			)}
			style={{ color: overrideColor ?? color }}
		/>
	)

	// An emoji brings its own colours; a tinted tile behind it only muddies them.
	const tinted = !icon?.startsWith(":")
	const backgroundStyle = tinted ? { backgroundColor: `${color}1A` } : undefined

	const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
		if (withBackground && tinted) {
			e.currentTarget.style.backgroundColor = `${color}26`
		}
	}

	const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
		if (withBackground && tinted) {
			e.currentTarget.style.backgroundColor = `${color}1A`
		}
	}

	if (withBackground) {
		return (
			<div
				className={cn(
					"flex h-8 w-8 items-center justify-center rounded",
					tinted
						? "transition-colors"
						: "hover:bg-gray-100 dark:hover:bg-gray-800",
				)}
				style={backgroundStyle}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<RoleCheck not={["VIEWER"]}>
					<ColorPicker
						withIcon
						withEmoji
						value={color}
						icon={icon}
						open={open}
						setOpen={setOpen}
						onChange={async (color) => {
							setColor(color)
							await mutateOk(
								zero.mutate(
									mutators.projects.update({
										id: project.id,
										color,
									}),
								),
							)
						}}
						onIconChange={async (icon) => {
							setIcon(icon)
							await mutateOk(
								zero.mutate(
									mutators.projects.update({
										id: project.id,
										icon,
									}),
								),
							)
						}}
					>
						<IconButton
							onClick={(e) => {
								e.preventDefault()
								setOpen(true)
							}}
							size="md"
							variant={iconVariant}
							className="h-full w-full bg-transparent hover:bg-transparent"
						>
							{iconElement}
						</IconButton>
					</ColorPicker>
				</RoleCheck>
				{role === "VIEWER" && iconElement}
			</div>
		)
	}

	return (
		<>
			<RoleCheck not={["VIEWER"]}>
				<ColorPicker
					withIcon
					withEmoji
					value={color}
					icon={icon}
					open={open}
					setOpen={setOpen}
					onChange={async (color) => {
						setColor(color)
						await mutateOk(
							zero.mutate(
								mutators.projects.update({
									id: project.id,
									color,
								}),
							),
						)
					}}
					onIconChange={async (icon) => {
						setIcon(icon)
						await mutateOk(
							zero.mutate(
								mutators.projects.update({
									id: project.id,
									icon,
								}),
							),
						)
					}}
				>
					<IconButton
						onClick={(e) => {
							e.preventDefault()
							setOpen(true)
						}}
						size="md"
						variant={iconVariant}
					>
						{iconElement}
					</IconButton>
				</ColorPicker>
			</RoleCheck>
			{role === "VIEWER" && iconElement}
		</>
	)
}
